import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InvoicePreviewModal = ({ invoice, onClose, onDownload, onSendEmail }) => {
  if (!invoice) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-slate-900 border-b border-slate-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white font-heading">Invoice Preview</h2>
            <p className="text-sm text-slate-400 mt-1 font-body">{invoice?.invoiceNumber}</p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              onClick={() => onDownload(invoice)}
              className="bg-blue-500 hover:bg-blue-600"
            >
              <Icon name="Download" size={18} />
              <span className="ml-2">Download PDF</span>
            </Button>
            <Button
              onClick={() => {
                onClose();
                onSendEmail(invoice);
              }}
              className="bg-green-500 hover:bg-green-600"
            >
              <Icon name="Mail" size={18} />
              <span className="ml-2">Send Email</span>
            </Button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-slate-800 transition-colors"
            >
              <Icon name="X" size={20} color="#94A3B8" />
            </button>
          </div>
        </div>

        {/* Invoice Content */}
        <div className="p-8 bg-white text-slate-900">
          {/* Company Header */}
          <div className="flex items-start justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 font-heading">Business Jadhak</h1>
              <p className="text-sm text-slate-600 mt-2 font-body">
                123 Business Street<br />
                Mumbai, Maharashtra 400001<br />
                India<br />
                contact@businessjadhak.com
              </p>
            </div>
            <div className="text-right">
              <div className="inline-block px-4 py-2 bg-amber-500 text-white rounded-lg">
                <p className="text-sm font-semibold font-body">INVOICE</p>
              </div>
              <p className="text-2xl font-bold text-slate-900 mt-3 font-heading">{invoice?.invoiceNumber}</p>
            </div>
          </div>

          {/* Invoice Details */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase mb-2 font-body">Bill To:</h3>
              <p className="text-base font-semibold text-slate-900 font-body">{invoice?.customerName}</p>
              <p className="text-sm text-slate-600 mt-1 font-body">
                {invoice?.customerAddress}<br />
                {invoice?.customerEmail}
              </p>
            </div>
            <div className="text-right">
              <div className="space-y-2">
                <div>
                  <span className="text-sm text-slate-500 font-body">Issue Date: </span>
                  <span className="text-sm font-medium text-slate-900 font-body">{new Date(invoice.issueDate)?.toLocaleDateString()}</span>
                </div>
                <div>
                  <span className="text-sm text-slate-500 font-body">Due Date: </span>
                  <span className="text-sm font-medium text-slate-900 font-body">{new Date(invoice.dueDate)?.toLocaleDateString()}</span>
                </div>
                {invoice?.paidDate && (
                  <div>
                    <span className="text-sm text-slate-500 font-body">Paid Date: </span>
                    <span className="text-sm font-medium text-green-600 font-body">{new Date(invoice.paidDate)?.toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="mb-8">
            <table className="w-full">
              <thead className="bg-slate-100 border-b-2 border-slate-300">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 font-body">Description</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700 font-body">Quantity</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700 font-body">Unit Price</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700 font-body">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {invoice?.items?.map((item, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-3 text-sm text-slate-900 font-body">{item?.description}</td>
                    <td className="px-4 py-3 text-center text-sm text-slate-900 font-body">{item?.quantity}</td>
                    <td className="px-4 py-3 text-right text-sm text-slate-900 font-body">₹{item?.unitPrice?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-sm text-slate-900 font-body">₹{item?.amount?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-8">
            <div className="w-64">
              <div className="flex justify-between py-2 border-b border-slate-200">
                <span className="text-sm text-slate-600 font-body">Subtotal:</span>
                <span className="text-sm font-medium text-slate-900 font-body">₹{invoice?.amount?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-200">
                <span className="text-sm text-slate-600 font-body">Tax (18%):</span>
                <span className="text-sm font-medium text-slate-900 font-body">₹{invoice?.taxAmount?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between py-3 bg-slate-100 px-3 rounded-lg mt-2">
                <span className="text-base font-semibold text-slate-900 font-body">Total:</span>
                <span className="text-base font-bold text-slate-900 font-body">₹{invoice?.totalAmount?.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Payment Terms & Notes */}
          <div className="border-t border-slate-200 pt-6">
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-2 font-body">Payment Terms:</h3>
              <p className="text-sm text-slate-600 font-body">{invoice?.paymentTerms}</p>
            </div>
            {invoice?.notes && (
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-2 font-body">Notes:</h3>
                <p className="text-sm text-slate-600 font-body">{invoice?.notes}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-slate-200 text-center">
            <p className="text-xs text-slate-500 font-caption">
              Thank you for your business! For any queries, please contact us at support@businessjadhak.com
            </p>
          </div>
        </div>

        {/* Status Badge */}
        {invoice?.status && (
          <div className="px-8 py-4 bg-slate-800 border-t border-slate-700">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400 font-body">Invoice Status:</span>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${
                invoice?.status === 'Paid' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                invoice?.status === 'Sent' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                invoice?.status === 'Viewed' ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' :
                invoice?.status === 'Overdue'? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-slate-500/20 text-slate-400 border-slate-500/30'
              }`}>
                {invoice?.status}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoicePreviewModal;